<template>
  <b-modal
    id="shell-switch-site-modal"
    ref="modal"
    title="Switch Site"
    hide-footer
    modal-class="switch-site-modal-tall"
    @hidden="onHidden"
  >
    <div data-test="app-shell-switch-site-panel">
      <p class="mb-3">Select a site to switch to:</p>
      <input
        v-if="userSites.length > 8"
        v-model="siteSearch"
        type="search"
        class="shell-site-search w-100 mb-2"
        placeholder="Search practices"
        aria-label="Search practices"
        data-test="shell-site-switcher-search"
        :disabled="siteSwitching"
      />
      <b-dropdown
        id="shell-site-switcher-dropdown"
        :text="selectedSiteDropdownText"
        variant="default"
        class="w-100 mb-3 site-switcher-dropdown"
        :disabled="siteSwitching"
      >
        <b-dropdown-text v-if="siteSearch && filteredUserSites.length === 0">
          No practices match "{{ siteSearch }}".
        </b-dropdown-text>
        <b-dropdown-item-button
          v-for="site in filteredUserSites"
          :key="site.SiteID"
          :active="selectedSite && selectedSite.SiteID === site.SiteID"
          @click="handleSiteSelection(site)"
        >
          <div class="option__desc">
            <span class="option__title fw-semi-bold">{{ siteCustomLabel(site) }}</span>
            <small v-if="site.CustomerName" class="d-block text-muted">{{ site.CustomerName }}</small>
          </div>
        </b-dropdown-item-button>
        <b-dropdown-divider v-if="userSites.length === 0" />
        <b-dropdown-text v-if="userSites.length === 0 && !siteLoading">
          No sites found.
        </b-dropdown-text>
        <b-dropdown-text v-if="userSites.length === 0 && siteLoading">
          Loading sites...
        </b-dropdown-text>
      </b-dropdown>

      <div class="d-flex justify-content-end mt-4">
        <b-button variant="default" class="mr-2" :disabled="siteSwitching" @click="hide">
          Cancel
        </b-button>
        <b-button variant="primary" :disabled="!selectedSite || siteSwitching" data-test="shell-switch-site-confirm" @click="confirmSwitchSite">
          <b-spinner v-if="siteSwitching" small class="mr-1" />
          {{ siteSwitching ? 'Switching...' : 'Switch to Selected Site' }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
/**
 * App shell — the Switch Site modal. A COPY of the classic header's (Header.vue switch-site
 * b-modal :419-473 and its methods loadUserSites :763, refreshSwitchableSites :807,
 * showSwitchSiteDialog, siteCustomLabel, confirmSwitchSite, switchToSite :857,
 * handleSiteSelection :930, hasMultipleSites). Header.vue is not refactored — it is what every
 * client sees; change both if the flow changes.
 *
 * AppShell owns open/close: `open` in (true ⇒ reload the site list, then show — or a toast when
 * there is only one site), `close` out when the modal hides. AppShell must close any popover
 * before opening this (b-modal 1040 < popovers 1050).
 *
 * emits close, multiple-sites(Boolean) — immediately and whenever it changes, so AppShell can
 *       gate the account menu's Switch (the classic `hasMultipleSites`);
 *       sites({ index, count }) — the current site's 1-based position (0 = not listed) and the
 *       number of sites, for the account menu's "N of M offices".
 */
import { mapActions, mapState } from 'vuex';
import { pickSiteList, upsertSiteRow, filterSites, resolveSwitchUser, switchErrorMessage } from '@/utils/siteSwitch';

const MODAL_ID = 'shell-switch-site-modal';

export default {
  name: 'AppSwitchSiteModal',

  props: {
    open: { type: Boolean, default: false },
  },

  data() {
    return {
      userSites: [],
      selectedSite: null,
      siteSearch: '',
      siteLoading: false,
      siteSwitching: false,
      sitesLoaded: false,
      siteCheckInterval: null,
    };
  },

  computed: {
    ...mapState({
      user: (state) => state.customer.user,
      currentSiteID: (state) => state.customer.siteID,
    }),

    // Header.vue `hasMultipleSites`, minus its side effect: the interval below re-reads storage.
    hasMultipleSites() {
      if (this.$store && this.$store.getters['customer/isDemo'] === true) return false; // a demo session never switches site
      return this.sitesLoaded && Array.isArray(this.userSites) && this.userSites.length > 1;
    },

    officePosition() {
      const list = Array.isArray(this.userSites) ? this.userSites.filter(Boolean) : [];
      const at = list.findIndex((site) => site.SiteID === this.currentSiteID);
      return { index: at + 1, count: list.length };
    },

    filteredUserSites() {
      return filterSites(this.userSites, this.siteSearch);
    },

    selectedSiteDropdownText() {
      return this.selectedSite ? this.siteCustomLabel(this.selectedSite) : 'Select Site';
    },
  },

  watch: {
    open(isOpen) {
      if (isOpen) this.showSwitchSiteDialog();
      else this.hide();
    },

    hasMultipleSites: {
      immediate: true,
      handler(v) {
        this.$emit('multiple-sites', v === true);
      },
    },

    // The account menu's "N of M offices": 1-based position of the current site (0 = not listed).
    officePosition: {
      immediate: true,
      handler(v) {
        this.$emit('sites', v);
      },
    },
  },

  // Header.vue created(): cached list now, the server's list once per mount, and a 1s re-read of
  // the cache until a second site shows up (Login.vue writes it after the first paint).
  created() {
    this.loadUserSites();
    this.refreshSwitchableSites();
    this.siteCheckInterval = setInterval(() => {
      if (!this.hasMultipleSites) this.loadUserSites();
      else clearInterval(this.siteCheckInterval);
    }, 1000);
  },

  beforeDestroy() {
    clearInterval(this.siteCheckInterval);
  },

  methods: {
    ...mapActions('customer', ['handleSelectSite', 'selectSite', 'getSwitchableSites']),

    toast(kind, msg) {
      try {
        if (this.$toast && typeof this.$toast[kind] === 'function') this.$toast[kind](msg);
      } catch (e) {
        /* no toast plugin (specs) */
      }
    },

    hide() {
      if (this.$bvModal) this.$bvModal.hide(MODAL_ID);
    },

    onHidden() {
      this.$emit('close');
    },

    // Header.vue loadUserSites :763.
    loadUserSites() {
      try {
        this.siteLoading = true;
        const storedSites = localStorage.getItem('userSites');
        if (storedSites && storedSites !== 'null' && storedSites !== 'undefined') {
          try {
            const parsedSites = JSON.parse(storedSites);
            this.userSites = Array.isArray(parsedSites) && parsedSites.length > 1
              ? pickSiteList(null, parsedSites)
              : [this.user];
          } catch (parseError) {
            console.error('Error parsing user sites:', parseError);
            this.userSites = [this.user];
          }
        } else {
          this.userSites = [this.user];
        }
        this.sitesLoaded = true;
        this.siteLoading = false;
        return Promise.resolve(this.userSites);
      } catch (error) {
        console.error('Error loading user sites:', error);
        this.siteLoading = false;
        this.userSites = [this.user];
        this.sitesLoaded = true;
        return Promise.reject(error);
      }
    },

    // Header.vue refreshSwitchableSites :807.
    async refreshSwitchableSites() {
      let serverSites;
      try {
        serverSites = await this.getSwitchableSites();
      } catch (e) {
        return;
      }
      if (!Array.isArray(serverSites) || serverSites.length === 0) return;
      try {
        localStorage.setItem('userSites', JSON.stringify(serverSites));
      } catch (e) {
        console.log('Could not cache switchable sites:', e && e.message);
      }
      this.loadUserSites();
    },

    // Header.vue showSwitchSiteDialog.
    showSwitchSiteDialog() {
      this.selectedSite = null;
      this.siteSearch = '';
      this.siteLoading = true;
      this.loadUserSites().catch(() => {}).finally(() => {
        this.siteLoading = false;
        if (this.hasMultipleSites) {
          if (this.$bvModal) this.$bvModal.show(MODAL_ID);
        } else {
          this.toast('info', 'You only have access to one site.');
          this.$emit('close');
        }
      });
    },

    siteCustomLabel(site) {
      if (!site) return '';
      const siteName = site.SiteName || site.Name || site.name;
      if (site.SiteID === this.currentSiteID) return `${siteName} (current)`;
      return siteName || 'Unknown Site';
    },

    handleSiteSelection(site) {
      this.selectedSite = site;
    },

    confirmSwitchSite() {
      if (this.selectedSite) this.switchToSite(this.selectedSite);
    },

    // Header.vue switchToSite :857.
    async switchToSite(site) {
      try {
        if (!site) {
          this.hide();
          return;
        }
        if (site.SiteID === this.currentSiteID) {
          this.hide();
          this.toast('info', "You're already on this site");
          return;
        }
        this.siteSwitching = true;
        // Server first: it sets the session for the new site and returns the row to log in with.
        const result = await this.selectSite({ SiteID: site.SiteID, EmployeeID: site.EmployeeID });
        const user = resolveSwitchUser(result, site);
        if (!user) {
          this.toast('error', switchErrorMessage(result) || 'There was an error switching to the site. Please try again later.');
          return;
        }
        try {
          const cached = JSON.parse(localStorage.getItem('userSites') || '[]');
          localStorage.setItem('userSites', JSON.stringify(upsertSiteRow(cached, user)));
        } catch (e) {
          console.log('Could not update cached sites:', e && e.message);
        }
        await this.handleSelectSite(user);
        this.hide();
      } catch (error) {
        console.error('Error switching site:', error);
        this.toast('error', 'There was an error switching to the site. Please try again later.');
      } finally {
        this.siteSwitching = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './shell';

// ⛔ ≥16px, in px: iOS Safari zooms on a focused input under 16px (frontend CLAUDE.md).
.shell-site-search {
  height: 44px;
  padding: 0 12px;
  border: 1px solid $fu-border-strong;
  border-radius: $fu-radius-md;
  background: $fu-surface;
  font-size: 16px;
  color: $fu-text;

  &:focus {
    outline: none;
    border-color: $fu-brand-300;
    box-shadow: $fu-ring;
  }
}

// Header.vue's cap: a Teamio operator sees every active practice (~30), so the menu scrolls
// inside the viewport instead of running off the bottom of the screen. No boundary="window" on the
// b-dropdown (Header.vue has one): it makes BootstrapVue add .position-static, so `width: 100%`
// measures the whole modal body and the menu ran 17px off a 375px phone screen.
.site-switcher-dropdown ::v-deep .dropdown-menu {
  width: 100%;
  max-height: 50vh;
  overflow-y: auto;
}
</style>
